'use client';

import { datadogRum } from '@datadog/browser-rum';
import { css } from '@repo/pandacss/css';
import { AnalyticsFeatureViewed } from '@repo/platform-analytics/AnalyticsFeatureViewed';
import { getOrCreateId } from '@repo/platform-utils/static-ids';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HEADER_ACTION_NAME, HEADER_ENTITY_NAME } from '../../../config/exploreOtherCardTypes';
import { useBranchId, useFunnelStepId } from '../../../model/Context/hooks';
import type { CardTypeOption } from '../../../model/exploreOtherCardTypes';
import { useBranchedPageAction } from '../../../model/useBranchedPageAction';
import { CloseFlow } from '../../CloseFlow';
import { CardTypePicker } from '../CardTypePicker';
import { CurrentBranchCopy } from './CurrentBranchCopy';
import { ExploreOtherOptionsButton } from './ExploreOtherOptionsButton';
export function ExploreOtherCardTypesBanner({
  options
}: {
  options: CardTypeOption[];
}) {
  const branchId = useBranchId();
  const stepId = useFunnelStepId();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const activityIdRef = useRef('');
  const activityId = getOrCreateId(activityIdRef);
  const fireExploreClickAction = useBranchedPageAction({
    actionName: HEADER_ACTION_NAME,
    actionSection: HEADER_ENTITY_NAME,
    actionType: 'click',
    actionInput: null,
    parentActivityId: activityId
  });
  const additionalAttributes = useMemo(() => ({
    page_branch_name: branchId
  }), [branchId]);
  const currentOption = options.find(option => option.branchId === branchId);
  useEffect(() => {
    if (!currentOption || !branchId) {
      datadogRum.addError(`No option found for branch id: ${branchId}`, {
        options
      });
    }
  }, [currentOption, branchId, options]);
  if (!branchId) return null;
  return <AnalyticsFeatureViewed activityId={activityId} entityName={HEADER_ENTITY_NAME} entitySection={`${branchId}_${stepId}`} additionalAttributes={additionalAttributes}>
      <div className={css({
      position: 'relative',
      display: 'flex',
      alignItems: {
        base: 'flex-start',
        md: 'center'
      },
      gap: '3',
      py: '2',
      _after: {
        content: '""',
        position: 'absolute',
        bottom: '[0]',
        left: '[calc(50% - 50cqw - max(0px, (100vw - var(--wrapper-max)) / 2))]',
        width: '[calc(100cqw + max(0px, (100vw - var(--wrapper-max)) / 2))]',
        borderBottom: '[1px solid {colors.neutral.lighter}]'
      }
    })}>
        <div className={css({
        flex: '[1]',
        minWidth: '[0]',
        display: 'flex',
        flexDirection: {
          base: 'column',
          md: 'row'
        },
        alignItems: 'center',
        justifyContent: {
          md: 'space-between'
        },
        gap: {
          base: '1',
          md: '2'
        }
      })}>
          {currentOption && <CurrentBranchCopy option={currentOption} />}
          <ExploreOtherOptionsButton onClick={() => {
          fireExploreClickAction();
          setIsPickerOpen(true);
        }} />
        </div>
        <div className={css({
        display: {
          base: 'flex',
          lg: 'none'
        },
        marginTop: {
          base: '[4px]',
          md: '[0]'
        }
      })}>
          <CloseFlow />
        </div>
        {isPickerOpen && <CardTypePicker options={options} currentBranchId={branchId} onClose={() => setIsPickerOpen(false)} />}
      </div>
    </AnalyticsFeatureViewed>;
}